import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getCountFromServer,
  query,
  Timestamp,
  updateDoc,
  where,
  orderBy,
  limit,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from './firebase';
import { getSite } from '../config/sites';

const collectionRef = (name) => collection(db, name);
const CACHE_TTL_MS = 120000; // 2 minutes — reduced Firestore reads vs 30s

const lookupCache = {
  nailTechs: { data: null, ts: 0 },
  services: { data: null, ts: 0 },
};

const appointmentListCache = { data: null, ts: 0 };

const isDateOnlyString = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

const parseDateOnlyLocal = (value) => {
  const [year, month, day] = String(value).split('-').map(Number);
  return new Date(year, month - 1, day);
};

const buildAppointmentDateTime = (dateValue, timeValue = '') => {
  const base = toDate(dateValue);
  if (!base || Number.isNaN(base.getTime())) return null;

  const dt = new Date(base);
  const [hh, mm] = String(timeValue || '')
    .split(':')
    .map((part) => Number(part));

  if (Number.isFinite(hh) && Number.isFinite(mm)) {
    dt.setHours(hh, mm, 0, 0);
  } else {
    dt.setHours(0, 0, 0, 0);
  }

  return dt;
};

const toDateKey = (value) => {
  if (!value) return '';
  if (isDateOnlyString(value)) return value;
  const dt = toDate(value);
  if (!dt || Number.isNaN(dt.getTime())) return '';
  const year = dt.getFullYear();
  const month = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (isDateOnlyString(value)) return parseDateOnlyLocal(value);
  return new Date(value);
};

const serialize = (docSnap) => ({ _id: docSnap.id, ...docSnap.data() });

const normalizeFirestoreData = (item) => {
  const normalized = { ...item };
  if (normalized.appointmentDate && typeof normalized.appointmentDate?.toDate === 'function') {
    normalized.appointmentDate = normalized.appointmentDate.toDate().toISOString();
  }
  if (normalized.reminderTime && typeof normalized.reminderTime?.toDate === 'function') {
    normalized.reminderTime = normalized.reminderTime.toDate().toISOString();
  }
  return normalized;
};

const toResponse = (data) => Promise.resolve({ data });

const normalizeNameValue = (value) => String(value || '').trim().toLowerCase();

const normalizePhoneValue = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (String(value || '').trim().startsWith('+')) return `+${digits}`;
  return `+${digits}`;
};

const toApiError = (error, fallback = 'Request failed') => {
  const message = error?.message || fallback;
  const wrapped = new Error(message);
  const code = error?.code || error?.details?.twilioCode || '';
  wrapped.response = { data: { error: message, code } };
  return wrapped;
};

// Admin actions are authorized by the signed-in Firebase user's identity, not a
// shared password. The caller's ID token is sent in the request doc and verified
// server-side against the admin allowlist.
const getAdminIdToken = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw toApiError({ message: 'Not signed in', code: 'unauthenticated' }, 'Not signed in');
  }
  try {
    return await user.getIdToken();
  } catch {
    throw toApiError(
      { message: 'Could not obtain session token', code: 'unauthenticated' },
      'Could not obtain session token'
    );
  }
};

const waitForRequestResult = async (collectionName, payload, timeoutMs = 25000) => {
  const requestRef = await addDoc(collectionRef(collectionName), {
    ...payload,
    status: 'pending',
    _requesterUid: auth.currentUser?.uid || null,
    createdAt: Timestamp.now(),
  });

  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => setTimeout(resolve, 1000));
    // eslint-disable-next-line no-await-in-loop
    const latest = await getDoc(doc(db, collectionName, requestRef.id));
    if (!latest.exists()) continue;
    const data = latest.data() || {};

    if (data.status === 'completed' || data.status === 'sent') {
      return data;
    }

    if (data.status === 'failed') {
      throw toApiError(
        { message: data.error || 'Request failed', code: data.code || '' },
        data.error || 'Request failed'
      );
    }
  }

  throw toApiError({ message: 'Request timed out', code: 'deadline-exceeded' }, 'Request timed out');
};

const isFresh = (ts) => Date.now() - ts < CACHE_TTL_MS;

const getLookupMap = async (name, force = false) => {
  const cached = lookupCache[name];
  if (!force && cached?.data && isFresh(cached.ts)) {
    return cached.data;
  }

  const snap = await getDocs(collectionRef(name));
  const data = snap.docs.reduce((acc, current) => {
    acc[current.id] = { _id: current.id, ...current.data() };
    return acc;
  }, {});

  if (cached) {
    cached.data = data;
    cached.ts = Date.now();
  }

  return data;
};

const enrichAppointments = async (rows) => {
  const [techs, services] = await Promise.all([getLookupMap('nailTechs'), getLookupMap('services')]);
  return rows.map((row) => {
    const normalized = normalizeFirestoreData(row);
    return {
      ...normalized,
      nailTechId: normalized.nailTechId ? techs[normalized.nailTechId] || null : null,
      serviceId: normalized.serviceId ? services[normalized.serviceId] || null : null,
    };
  });
};

const invalidateAppointmentCache = () => {
  appointmentListCache.data = null;
  appointmentListCache.ts = 0;
};

const defaultSettings = {
  twilioConfigured: false,
  twilioPhoneNumber: '',
  reminderMessageTemplate: 'Hi {name}, reminder: appointment at {time} on {date}. Reply STOP to opt out.',
  smsSchedule: {
    sendConfirmationOnCreate: false,
    send24hReminder: true,
    send1hReminder: false,
  },
  allowedAdminEmails: [],
  allowedAdminUids: [],
  privacyPolicyContent: '',
  termsOfServiceContent: '',
};

const normalizeSmsSchedule = (input) => ({
  sendConfirmationOnCreate: Boolean(input?.sendConfirmationOnCreate),
  send24hReminder: input?.send24hReminder !== false,
  send1hReminder: Boolean(input?.send1hReminder),
});

export const appointmentAPI = {
  create: async (data) => {
    try {
      const appointmentDate = toDate(data.appointmentDate);
      const appointmentDateTime = buildAppointmentDateTime(data.appointmentDate, data.startTime);
      const appointmentDateKey = toDateKey(data.appointmentDate);
      let smsSchedule = { ...defaultSettings.smsSchedule };

      try {
        const settingsSnap = await getDoc(doc(db, 'settings', 'app'));
        if (settingsSnap.exists()) {
          smsSchedule = normalizeSmsSchedule(settingsSnap.data().smsSchedule);
        }
      } catch (settingsError) {
        // Keep default schedule options if settings cannot be loaded.
      }

      const leadMinutes = [];
      if (smsSchedule.send24hReminder) leadMinutes.push(1440);
      if (smsSchedule.send1hReminder) leadMinutes.push(60);
      const maxLead = leadMinutes.length ? Math.max(...leadMinutes) : 0;
      // Only set a reminder window when booked more than 24h out — short-lead
      // bookings get the confirmation but no 24h reminder (matches the Express
      // path and the scheduler guard in wasBookedMoreThan24hOut).
      const bookedMoreThan24hOut = appointmentDateTime && maxLead > 0
        && appointmentDateTime.getTime() - Date.now() > 24 * 60 * 60 * 1000;
      const reminderTime = bookedMoreThan24hOut
        ? new Date(appointmentDateTime.getTime() - maxLead * 60 * 1000)
        : null;

      const payload = {
        clientName: data.clientName,
        clientPhone: data.clientPhone || '',
        nailTechId: data.nailTechId || null,
        serviceId: data.serviceId || null,
        appointmentDate: appointmentDate ? Timestamp.fromDate(appointmentDate) : null,
        appointmentDateKey,
        startTime: data.startTime,
        endTime: data.endTime || '',
        notes: data.notes || '',
        status: 'scheduled',
        deposit: data.deposit || { status: 'none', amount: 0, currency: 'usd', method: '' },
        reminderSent: false,
        smsSchedule,
        reminderLeadMinutes: maxLead,
        reminderSentStages: {
          confirmation: false,
          before24h: false,
          before1h: false,
        },
        reminderTime: reminderTime ? Timestamp.fromDate(reminderTime) : null,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const created = await addDoc(collectionRef('appointments'), payload);
      const createdDoc = await getDoc(doc(db, 'appointments', created.id));
      invalidateAppointmentCache();
      return toResponse(normalizeFirestoreData(serialize(createdDoc)));
    } catch (error) {
      throw toApiError(error, 'Failed to create appointment');
    }
  },

  getAll: async (opts = {}) => {
    try {
      const cached = appointmentListCache.data;
      if (cached && isFresh(appointmentListCache.ts) && !opts.force) {
        return toResponse(cached);
      }

      const maxLimit = opts.limit || 500;
      const constraints = [orderBy('appointmentDate', 'asc'), limit(maxLimit)];

      const snap = await getDocs(query(collectionRef('appointments'), ...constraints));
      const rows = snap.docs.map((item) => serialize(item));
      const enriched = await enrichAppointments(rows);
      appointmentListCache.data = enriched;
      appointmentListCache.ts = Date.now();
      return toResponse(enriched);
    } catch (error) {
      throw toApiError(error, 'Failed to fetch appointments');
    }
  },

  /** Lightweight count — uses Firestore count aggregation (1 read, not N). */
  getCount: async () => {
    try {
      const snap = await getCountFromServer(collectionRef('appointments'));
      return toResponse({ count: snap.data().count });
    } catch (error) {
      // Fallback: fetch and count (more expensive but works without aggregation)
      const all = await appointmentAPI.getAll({ fields: ['status'], limit: 9999 });
      return toResponse({ count: (all.data || []).length });
    }
  },

  getByDateRange: async (startDate, endDate) => {
    try {
      const start = Timestamp.fromDate(new Date(startDate));
      const end = Timestamp.fromDate(new Date(endDate));
      const snap = await getDocs(
        query(
          collectionRef('appointments'),
          where('appointmentDate', '>=', start),
          where('appointmentDate', '<=', end),
          orderBy('appointmentDate', 'asc')
        )
      );
      const rows = snap.docs.map((item) => serialize(item));
      const enriched = await enrichAppointments(rows);
      return toResponse(enriched);
    } catch (error) {
      throw toApiError(error, 'Failed to fetch appointments by range');
    }
  },

  update: async (id, data) => {
    try {
      const target = doc(db, 'appointments', id);
      const updatePayload = { ...data, updatedAt: Timestamp.now() };
      if (data.appointmentDate) {
        updatePayload.appointmentDate = Timestamp.fromDate(toDate(data.appointmentDate));
        updatePayload.appointmentDateKey = toDateKey(data.appointmentDate);
      }
      await updateDoc(target, updatePayload);
      const latest = await getDoc(target);
      invalidateAppointmentCache();
      return toResponse(normalizeFirestoreData(serialize(latest)));
    } catch (error) {
      throw toApiError(error, 'Failed to update appointment');
    }
  },

  cancel: async (id) => {
    try {
      const target = doc(db, 'appointments', id);
      await updateDoc(target, { status: 'cancelled', updatedAt: Timestamp.now() });
      const latest = await getDoc(target);
      invalidateAppointmentCache();
      return toResponse(normalizeFirestoreData(serialize(latest)));
    } catch (error) {
      throw toApiError(error, 'Failed to cancel appointment');
    }
  },

  checkIn: async (id) => {
    try {
      const target = doc(db, 'appointments', id);
      await updateDoc(target, { status: 'completed', checkedInAt: Timestamp.now(), updatedAt: Timestamp.now() });
      const latest = await getDoc(target);
      invalidateAppointmentCache();
      return toResponse(normalizeFirestoreData(serialize(latest)));
    } catch (error) {
      throw toApiError(error, 'Failed to check in appointment');
    }
  },

  sendReminder: async (id) => {
    try {
      const requestResult = await waitForRequestResult(
        'smsRequests',
        {
          type: 'reminder',
          appointmentId: id,
          idToken: await getAdminIdToken(),
        },
        30000
      );

      return toResponse({ ok: true, sid: requestResult.sid || 'OK', status: 'sent' });
    } catch (error) {
      throw toApiError(error, 'Failed to send reminder');
    }
  },

  reschedule: async (id, newDate, newTime) => {
    try {
      const target = doc(db, 'appointments', id);
      const dateObj = toDate(newDate);
      const updatePayload = {
        appointmentDate: dateObj ? Timestamp.fromDate(dateObj) : null,
        appointmentDateKey: toDateKey(newDate),
        startTime: newTime,
        reminderSent: false,
        reminderSentStages: {
          confirmation: false,
          before24h: false,
          before1h: false,
        },
        updatedAt: Timestamp.now(),
      };
      await updateDoc(target, updatePayload);
      const latest = await getDoc(target);
      invalidateAppointmentCache();

      // Send reschedule notification SMS via smsRequests Firestore trigger
      try {
        await waitForRequestResult(
          'smsRequests',
          { type: 'reschedule', appointmentId: id, idToken: await getAdminIdToken() },
          20000
        );
      } catch (smsError) {
        // Non-fatal: appointment is rescheduled even if SMS fails
        console.warn('Reschedule SMS failed:', smsError?.message);
      }

      return toResponse(normalizeFirestoreData(serialize(latest)));
    } catch (error) {
      throw toApiError(error, 'Failed to reschedule appointment');
    }
  },
};

export const nailTechAPI = {
  create: async (data) => {
    try {
      const created = await addDoc(collectionRef('nailTechs'), {
        ...data,
        isActive: data.isActive ?? true,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      const latest = await getDoc(doc(db, 'nailTechs', created.id));
      return toResponse(serialize(latest));
    } catch (error) {
      throw toApiError(error, 'Failed to create nail tech');
    }
  },
  getAll: async () => {
    try {
      const snap = await getDocs(query(collectionRef('nailTechs'), where('isActive', '==', true)));
      return toResponse(snap.docs.map((item) => serialize(item)));
    } catch (error) {
      throw toApiError(error, 'Failed to fetch nail techs');
    }
  },
  getById: async (id) => {
    try {
      const item = await getDoc(doc(db, 'nailTechs', id));
      return toResponse(item.exists() ? serialize(item) : null);
    } catch (error) {
      throw toApiError(error, 'Failed to fetch nail tech');
    }
  },
  update: async (id, data) => {
    try {
      const target = doc(db, 'nailTechs', id);
      await updateDoc(target, { ...data, updatedAt: Timestamp.now() });
      const latest = await getDoc(target);
      return toResponse(serialize(latest));
    } catch (error) {
      throw toApiError(error, 'Failed to update nail tech');
    }
  },
};

export const serviceAPI = {
  create: async (data) => {
    try {
      const created = await addDoc(collectionRef('services'), {
        ...data,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      const latest = await getDoc(doc(db, 'services', created.id));
      return toResponse(serialize(latest));
    } catch (error) {
      throw toApiError(error, 'Failed to create service');
    }
  },
  getAll: async () => {
    try {
      const snap = await getDocs(collectionRef('services'));
      return toResponse(snap.docs.map((item) => serialize(item)));
    } catch (error) {
      throw toApiError(error, 'Failed to fetch services');
    }
  },
  getById: async (id) => {
    try {
      const item = await getDoc(doc(db, 'services', id));
      return toResponse(item.exists() ? serialize(item) : null);
    } catch (error) {
      throw toApiError(error, 'Failed to fetch service');
    }
  },
  update: async (id, data) => {
    try {
      const target = doc(db, 'services', id);
      await updateDoc(target, { ...data, updatedAt: Timestamp.now() });
      const latest = await getDoc(target);
      return toResponse(serialize(latest));
    } catch (error) {
      throw toApiError(error, 'Failed to update service');
    }
  },
  delete: async (id) => {
    try {
      await deleteDoc(doc(db, 'services', id));
      return toResponse({ message: 'Service deleted' });
    } catch (error) {
      throw toApiError(error, 'Failed to delete service');
    }
  },
};

export const clientAPI = {
  create: async (data) => {
    try {
      const name = String(data.name || '').trim();
      const phone = normalizePhoneValue(data.phone || '');
      const created = await addDoc(collectionRef('clients'), {
        ...data,
        name,
        phone,
        normalizedName: normalizeNameValue(name),
        normalizedPhone: phone,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      const latest = await getDoc(doc(db, 'clients', created.id));
      return toResponse(serialize(latest));
    } catch (error) {
      throw toApiError(error, 'Failed to create client');
    }
  },
  getAll: async () => {
    try {
      const snap = await getDocs(query(collectionRef('clients'), orderBy('createdAt', 'desc')));
      return toResponse(snap.docs.map((item) => serialize(item)));
    } catch (error) {
      throw toApiError(error, 'Failed to fetch clients');
    }
  },
  getById: async (id) => {
    try {
      const item = await getDoc(doc(db, 'clients', id));
      return toResponse(item.exists() ? serialize(item) : null);
    } catch (error) {
      throw toApiError(error, 'Failed to fetch client');
    }
  },
  update: async (id, data) => {
    try {
      const target = doc(db, 'clients', id);
      const updatePayload = { ...data, updatedAt: Timestamp.now() };
      if (Object.prototype.hasOwnProperty.call(data, 'name')) {
        const name = String(data.name || '').trim();
        updatePayload.name = name;
        updatePayload.normalizedName = normalizeNameValue(name);
      }
      if (Object.prototype.hasOwnProperty.call(data, 'phone')) {
        const phone = normalizePhoneValue(data.phone || '');
        updatePayload.phone = phone;
        updatePayload.normalizedPhone = phone;
      }
      await updateDoc(target, updatePayload);
      const latest = await getDoc(target);
      return toResponse(serialize(latest));
    } catch (error) {
      throw toApiError(error, 'Failed to update client');
    }
  },
  findByPhone: async (phone) => {
    try {
      const normalizedPhone = normalizePhoneValue(phone);
      if (!normalizedPhone) return toResponse(null);

      const byPhoneSnap = await getDocs(
        query(collectionRef('clients'), where('phone', '==', normalizedPhone))
      );
      let docs = byPhoneSnap.docs;

      if (!docs.length) {
        const byNormalizedPhoneSnap = await getDocs(
          query(collectionRef('clients'), where('normalizedPhone', '==', normalizedPhone))
        );
        docs = byNormalizedPhoneSnap.docs;
      }

      return toResponse(docs.length ? serialize(docs[0]) : null);
    } catch (error) {
      throw toApiError(error, 'Failed to lookup client by phone');
    }
  },
  findByNamePhone: async (name, phone) => {
    try {
      const normalizedName = normalizeNameValue(name);
      const normalizedPhone = normalizePhoneValue(phone);
      if (!normalizedName || !normalizedPhone) return toResponse(null);

      const byPhoneSnap = await getDocs(
        query(collectionRef('clients'), where('phone', '==', normalizedPhone))
      );
      let docs = byPhoneSnap.docs;

      if (!docs.length) {
        const byNormalizedPhoneSnap = await getDocs(
          query(collectionRef('clients'), where('normalizedPhone', '==', normalizedPhone))
        );
        docs = byNormalizedPhoneSnap.docs;
      }

      const found = docs.find((item) => {
        const row = item.data() || {};
        const rowName = normalizeNameValue(row.normalizedName || row.name || '');
        return rowName === normalizedName;
      });

      return toResponse(found ? serialize(found) : null);
    } catch (error) {
      throw toApiError(error, 'Failed to lookup client');
    }
  },
  ensureByNamePhone: async (name, phone) => {
    try {
      const found = await clientAPI.findByNamePhone(name, phone);
      if (found.data) return found;
      return clientAPI.create({ name, phone });
    } catch (error) {
      throw toApiError(error, 'Failed to ensure client');
    }
  },
  delete: async (id) => {
    try {
      await deleteDoc(doc(db, 'clients', id));
      return toResponse({ id });
    } catch (error) {
      throw toApiError(error, 'Failed to delete client');
    }
  },
  getVisits: async (clientPhone) => {
    try {
      const phone = normalizePhoneValue(clientPhone || '');
      if (!phone) return toResponse([]);
      // Use only the equality filter — no orderBy — to avoid requiring a
      // composite Firestore index. Sort the results in JS instead.
      const snap = await getDocs(
        query(
          collectionRef('appointments'),
          where('clientPhone', '==', phone)
        )
      );
      const rows = snap.docs
        .map((d) => ({ _id: d.id, ...normalizeFirestoreData(d.data()) }))
        .sort((a, b) => {
          const ta = a.appointmentDate ? new Date(a.appointmentDate).getTime() : 0;
          const tb = b.appointmentDate ? new Date(b.appointmentDate).getTime() : 0;
          return tb - ta; // newest first
        });
      return toResponse(rows);
    } catch (error) {
      throw toApiError(error, 'Failed to fetch visits');
    }
  },
  checkIn: async (client) => {
    try {
      const today = new Date();
      const dateKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const entry = {
        clientId: client._id || '',
        clientName: client.name || '',
        clientPhone: normalizePhoneValue(client.phone || ''),
        date: dateKey,
        checkedInAt: Timestamp.now(),
      };
      const ref = await addDoc(collectionRef('checkins'), entry);
      return toResponse({ _id: ref.id, ...entry });
    } catch (error) {
      throw toApiError(error, 'Failed to check in client');
    }
  },
};

export const adminAPI = {
  getSettings: async () => {
    try {
      const requestResult = await waitForRequestResult('adminRequests', {
        type: 'getSettings',
        idToken: await getAdminIdToken(),
      });

      const result = requestResult.result || {};
      return toResponse({
        ...defaultSettings,
        ...result,
        smsSchedule: normalizeSmsSchedule(result.smsSchedule),
        allowedAdminEmails: Array.isArray(result.allowedAdminEmails) ? result.allowedAdminEmails : [],
        allowedAdminUids: Array.isArray(result.allowedAdminUids) ? result.allowedAdminUids : [],
      });
    } catch (error) {
      throw toApiError(error, 'Failed to get admin settings');
    }
  },
  updateSettings: async (data) => {
    try {
      const requestResult = await waitForRequestResult('adminRequests', {
        type: 'updateSettings',
        idToken: await getAdminIdToken(),
        settings: {
          reminderMessageTemplate: data.reminderMessageTemplate || '',
          smsSchedule: normalizeSmsSchedule(data.smsSchedule),
          privacyPolicyContent: data.privacyPolicyContent || '',
          termsOfServiceContent: data.termsOfServiceContent || '',
        },
      });

      return toResponse(requestResult.result || { ok: true, message: 'Settings updated' });
    } catch (error) {
      throw toApiError(error, 'Failed to update admin settings');
    }
  },
  sendTestSMS: async (to, message) => {
    try {
      const requestResult = await waitForRequestResult(
        'smsRequests',
        {
        type: 'test',
        to,
        message,
        idToken: await getAdminIdToken(),
        },
        30000
      );

      return toResponse({ ok: true, sid: requestResult.sid || 'OK', status: 'sent' });
    } catch (error) {
      const twilioMessage =
        (typeof error?.details === 'string' && error.details) ||
        error?.details?.twilioMessage ||
        error?.message ||
        error?.response?.data?.error ||
        'Failed to send test SMS';
      const twilioCode = error?.details?.twilioCode || error?.code || '';
      throw toApiError({ message: twilioMessage, code: twilioCode }, twilioMessage);
    }
  },

  processDueReminders: async (databaseId) => {
    try {
      const requestResult = await waitForRequestResult(
        'adminRequests',
        {
          type: 'processDueReminders',
          idToken: await getAdminIdToken(),
          databaseId: databaseId || '',
        },
        60000
      );
      return toResponse(requestResult.result || { ok: true });
    } catch (error) {
      throw toApiError(error, 'Failed to process due reminders');
    }
  },

  /** Update the salon's deposit payment details (Zelle / CashApp). */
  updateDepositSettings: async (data) => {
    try {
      const fn = httpsCallable(functions, 'updateDepositSettings');
      const res = await fn({
        ...data,
        databaseId: depositAPI.getDatabaseId(),
      });
      return toResponse(res.data || { ok: true });
    } catch (error) {
      throw toApiError(error, 'Failed to update deposit settings');
    }
  },
};

export const legalContentAPI = {
  getPublicContent: async () => {
    try {
      const settingsSnap = await getDoc(doc(db, 'settings', 'app'));
      const settings = settingsSnap.exists() ? settingsSnap.data() : {};
      return toResponse({
        privacyPolicyContent: settings.privacyPolicyContent || defaultSettings.privacyPolicyContent,
        termsOfServiceContent: settings.termsOfServiceContent || defaultSettings.termsOfServiceContent,
      });
    } catch (error) {
      throw toApiError(error, 'Failed to load legal content');
    }
  },
};

/**
 * Deposit / payment API — deposits are collected via Zelle or CashApp
 * (peer-to-peer, no checkout API). The client is shown the salon's payment
 * details, pays in their app, then confirms — which marks the deposit as
 * 'pending' for staff to verify and mark 'paid'.
 */
export const depositAPI = {
  getDatabaseId: () => getSite().databaseId,

  /** Compute the deposit amount for a service based on its config. */
  computeDeposit: (service) => {
    const deposit = service?.deposit;
    if (!deposit || !deposit.enabled) return null;
    const price = Number(service?.price || 0);
    let amount = 0;
    if (deposit.type === 'percent') {
      amount = Math.round((price * Number(deposit.value || 0)) / 100);
    } else {
      amount = Math.round(Number(deposit.value || 0));
    }
    if (amount <= 0) return null;
    return {
      enabled: true,
      type: deposit.type,
      value: Number(deposit.value || 0),
      amount,
      currency: 'usd',
      status: 'required',
      method: '',
    };
  },

  /** Read the salon's public payment methods (Zelle / CashApp) from settings/app. */
  getPublicMethods: async () => {
    try {
      const snap = await getDoc(doc(db, 'settings', 'app'));
      const s = snap.exists() ? snap.data() : {};
      return toResponse({
        zelleEmail: s.zelleEmail || '',
        zellePhone: s.zellePhone || '',
        cashappTag: s.cashappTag || '',
        depositNote: s.depositNote || '',
      });
    } catch (error) {
      return toResponse({ zelleEmail: '', zellePhone: '', cashappTag: '', depositNote: '' });
    }
  },

  /** Count appointments by deposit status (required / pending / paid). */
  getStats: async () => {
    try {
      const statuses = ['required', 'pending', 'paid'];
      const stats = { required: 0, pending: 0, paid: 0 };
      for (const status of statuses) {
        const snap = await getCountFromServer(
          query(collectionRef('appointments'), where('deposit.status', '==', status))
        );
        stats[status] = snap.data().count;
      }
      return toResponse(stats);
    } catch (error) {
      return toResponse({ required: 0, pending: 0, paid: 0 });
    }
  },

  /** Client confirms they sent the deposit via Zelle or CashApp. */
  confirmPayment: async (appointmentId, method) => {
    try {
      const fn = httpsCallable(functions, 'confirmWebsiteDeposit');
      const res = await fn({
        appointmentId,
        databaseId: depositAPI.getDatabaseId(),
        method,
      });
      return toResponse(res.data || {});
    } catch (error) {
      throw toApiError(error, 'Failed to confirm deposit');
    }
  },

  /** Set deposit status manually (mark paid / refund / forfeit). */
  setStatus: async (appointmentId, status) => {
    try {
      const updates = { updatedAt: Timestamp.now() };
      if (status === 'paid') {
        updates['deposit.status'] = 'paid';
        updates['deposit.method'] = updates['deposit.method'] || 'manual';
        updates['deposit.paidAt'] = Timestamp.now();
      } else if (status === 'refunded') {
        updates['deposit.status'] = 'refunded';
        updates['deposit.refundedAt'] = Timestamp.now();
      } else if (status === 'forfeited') {
        updates['deposit.status'] = 'forfeited';
        updates['deposit.refundedAt'] = null;
      }
      await updateDoc(doc(db, 'appointments', appointmentId), updates);
      return toResponse({ ok: true, status });
    } catch (error) {
      throw toApiError(error, 'Failed to update deposit status');
    }
  },
};

